import dayjs from 'dayjs';

import { cn } from '@rocco/utils/cn';
import { BookMeetingGatePane } from '~/modules-rocco/booking/components/BookMeetingGatePane';
import {
    GateHeading,
    GateSubcopy,
} from '~/modules-rocco/user/component/auth-modal/GateHeading';
import { GATE_PANE_BRAND_DEFAULT } from '~/modules-rocco/user/auth-gate/gate-pane-image';
import { MINI_BODY_CLASS, MINI_PANE_CLASS } from './ModalPeek';

export interface BookMeetingPeekSubject {
    /** The practice's banner, behind the pane's copy. */
    image?: string;
    practice: string;
    companyLogo?: string | null;
    logoBackgroundColor?: string | null;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export interface BookMeetingPeekSubject {
    /** The practice's banner, behind the pane's copy. */
    image?: string;
    practice: string;
    companyLogo?: string | null;
    logoBackgroundColor?: string | null;
}

/**
 * This month, as the gate's compact calendar lays it out: a Monday-first grid
 * with the leading blanks, and nothing to pick. Days before today are greyed
 * as the real grid greys them.
 */
const MiniMonth = () => {
    const today = dayjs();
    const first = today.startOf('month');
    const leading = (first.day() + 6) % 7;
    const cells = Array.from(
        { length: leading + first.daysInMonth() },
        (_, index) => (index < leading ? null : index - leading + 1),
    );

    return (
        <div className="rounded-[1rem] bg-[#f6f6f6] px-3 py-3">
            <div className="mb-2 flex items-center justify-between border-b border-black/8 px-2 pb-2">
                <span className="text-[0.9375rem] font-medium text-[#181818]">
                    {today.format('MMMM YYYY')}
                </span>
                <span className="flex gap-2 text-black/40">
                    <span aria-hidden="true">‹</span>
                    <span aria-hidden="true">›</span>
                </span>
            </div>
            <div className="grid grid-cols-7">
                {WEEKDAYS.map((day) => (
                    <div
                        key={day}
                        className="flex h-[1.3125rem] items-center justify-center text-[0.6875rem] text-[#828282]"
                    >
                        {day}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7">
                {cells.map((day, index) => (
                    <div
                        key={index}
                        className={cn(
                            'py-1.5 text-center text-3.5',
                            day === null
                                ? 'invisible'
                                : day < today.date()
                                  ? 'text-black/30'
                                  : 'text-[#181818]',
                        )}
                    >
                        {day ?? 0}
                    </div>
                ))}
            </div>
        </div>
    );
};

/**
 * The booking gate as it opens, drawn small for {@link ModalPeek}: the
 * practice's pane on the left, the first step on the right.
 */
export const BookMeetingMiniModal = ({
    subject,
}: {
    subject: BookMeetingPeekSubject;
}) => (
    <>
        <div className={MINI_PANE_CLASS}>
            <BookMeetingGatePane
                image={subject.image ?? GATE_PANE_BRAND_DEFAULT}
                subjectName={subject.practice}
                professionalName={subject.practice}
                isCompany
                companyLogo={subject.companyLogo}
                logoBackgroundColor={subject.logoBackgroundColor}
            />
        </div>
        <div className={MINI_BODY_CLASS}>
            <div className="mx-auto my-auto flex h-full w-full max-w-90.75 flex-col">
                <GateHeading className="mt-3">Book a Meeting</GateHeading>
                <GateSubcopy>
                    Pick a day that suits and the team at {subject.practice}{' '}
                    will be in touch to confirm.
                </GateSubcopy>
                <div className="mt-6">
                    <MiniMonth />
                </div>
                <div className="flex-1" />
                <div className="flex h-14 w-full items-center justify-center rounded-[var(--radius-btn-l)] bg-[#181818] text-[1.125rem] font-medium tracking-[-0.02em] text-[#f3f3f3] shadow-[0_0.1875rem_0.8125rem_0_rgba(0,0,0,0.25),0_1.25rem_2.125rem_0_rgba(0,0,0,0.25)]">
                    Continue
                </div>
            </div>
        </div>
    </>
);
